import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { T } from './translations';
import FaqPage from './FaqPage';
import GlobalCarePage from './pages/GlobalCarePage';
import HomePage from './pages/HomePage';
import PasantiasPage from './pages/PasantiasPage';
import PrivacyPage from './pages/PrivacyPage';
import lpcLogo from './images/main/LPClogo_White.png';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  navy:       '#1B2437',   // primary dark (nav, dark sections)
  navyMid:    '#223049',
  navyLight:  '#2c3e5e',
  orange:     '#F59420',   // primary accent (buttons, highlights)
  orangeHover:'#F7A840',
  white:      '#ffffff',
  off:        '#f8f9fa',
  offDark:    '#f0f2f5',
  border:     '#e5e7eb',
  textDark:   '#111827',   // heavy headlines
  text:       '#374151',   // body text
  muted:      '#6b7280',
  success:    '#22c55e',
  r:          '8px',
};

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const IconWA = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 26 26" fill="white" aria-label="WhatsApp" role="img">
    <path d="M13 .5C6.1.5.5 6.1.5 13c0 2.3.6 4.5 1.7 6.4L.5 25.5l6.3-2c1.8 1 3.8 1.5 6.2 1.5C19.9 25 25.5 19.4 25.5 12.5 25.5 5.6 19.9.5 13 .5zm0 22.5c-1.9 0-3.8-.5-5.4-1.4l-.4-.2-4 1.2 1.2-4-.3-.4C3.2 16.5 2.7 14.8 2.7 12.9 2.7 7.3 7.4 2.7 13 2.7c5.6 0 10.3 4.6 10.3 10.3 0 5.7-4.7 10-10.3 10zm5.7-7.7c-.3-.2-1.8-.9-2-.9-.3-.1-.5-.1-.7.2-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.5-.9-.8-1.5-1.8-1.6-2-.2-.3 0-.5.1-.6.1-.2.3-.4.4-.5.1-.2.2-.3.2-.5 0-.2-.6-1.5-.8-2-.2-.5-.5-.4-.7-.4h-.6c-.2 0-.5.1-.7.4-.3.3-1 1-1 2.4s1 2.8 1.2 3.1c.2.2 2 3.1 4.8 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.1-1.3-.1-.1-.3-.2-.6-.4z" />
  </svg>
);

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [lang, setLang]       = useState('es');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);

  const t = T[lang];
  const closeNav = () => { setNavOpen(false); setDropOpen(false); };

  const languages = [
    { code: 'es', flag: '🇨🇴', label: 'Español' },
    { code: 'nl', flag: '🇳🇱', label: 'Nederlands' },
    { code: 'en', flag: '🇺🇸', label: 'English' },
  ];

  useEffect(() => {
    if (!langOpen) return;
    const handleOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [langOpen]);

  // Chatbase: set greeting in site's selected language so bot responds in same language
  useEffect(() => {
    const greetings = {
      es: ['¡Hola! Soy el asistente de LPC Talent.', '¿En qué puedo ayudarte? Responde en español.'],
      nl: ['Hallo! Ik ben de assistent van LPC Talent.', 'Waar kan ik je mee helpen? Antwoord in het Nederlands.'],
      en: ['Hi! I\'m the LPC Talent assistant.', 'How can I help you? Please respond in English.'],
    };
    if (typeof window !== 'undefined' && window.chatbase) {
      window.chatbase('setInitialMessages', greetings[lang] || greetings.en);
    }
  }, [lang]);

  const soyItems = lang === 'es'
    ? [
        { label: 'Profesional de la salud',           href: '/globalcare' },
        { label: 'Estudiante de tecnología',           href: '/pasantias' },
        { label: 'Institución educativa de salud',     href: '/#contact' },
        { label: 'Institución educativa tecnológica',  href: '/#contact' },
      ]
    : lang === 'nl'
    ? [
        { label: 'Zorgprofessional',                    href: '/globalcare' },
        { label: 'Technologiestudent',                  href: '/pasantias' },
        { label: 'Onderwijsinstelling zorg',            href: '/#contact' },
        { label: 'Technologische onderwijsinstelling',  href: '/#contact' },
      ]
    : [
        { label: 'Healthcare professional',             href: '/globalcare' },
        { label: 'Technology student',                  href: '/pasantias' },
        { label: 'Healthcare educational institution',  href: '/#contact' },
        { label: 'Technology educational institution',  href: '/#contact' },
      ];

  const soyLabel = lang === 'es' ? 'Soy' : lang === 'nl' ? 'Ik ben' : 'I am';

  return (
    <BrowserRouter>
    <div style={{ fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif", color: C.text, background: C.white }}>

      {/* ════════════════════════════════════════════════════ TOPBAR */}
      <div className="topbar" style={{
        background: C.navy, padding: '11px 48px 7px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <a href="https://wa.me/573059469665" target="_blank" rel="noopener noreferrer"
          style={{ color: C.orange, fontSize: 12, letterSpacing: 0.4, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconWA size={16} />
          {t.topbar_wa} +57 305 946 9665
        </a>
        <div ref={langRef} style={{ position: 'relative' }}>
          <button onClick={() => setLangOpen(o => !o)} style={{
            background: C.navy, border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 4, padding: '5px 10px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            color: C.white, fontSize: 12, fontFamily: 'inherit',
          }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>{languages.find(l => l.code === lang)?.flag}</span>
            <span>{languages.find(l => l.code === lang)?.label}</span>
            <span style={{ fontSize: 10, opacity: 0.8 }}>▾</span>
          </button>
          {langOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0,
              backgroundColor: C.white, border: `1px solid ${C.border}`,
              borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
              overflow: 'hidden', zIndex: 300, minWidth: 160,
            }}>
              {languages.map(l => (
                <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '10px 14px', background: lang === l.code ? 'rgba(245,148,32,0.15)' : 'none',
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
                    color: C.textDark, textAlign: 'left',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = lang === l.code ? 'rgba(245,148,32,0.15)' : C.off}
                  onMouseLeave={e => e.currentTarget.style.background = lang === l.code ? 'rgba(245,148,32,0.15)' : 'transparent'}>
                  <span style={{ fontSize: 18 }}>{l.flag}</span>
                  {l.label}
                  {lang === l.code && <span style={{ marginLeft: 'auto', color: C.orange, fontSize: 16 }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════ NAV */}
      <nav className="nav-bar" style={{
        background: C.navy, padding: '0 48px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        height: 84, position: 'sticky', top: 0, zIndex: 200,
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
        overflow: 'visible',
      }}>
        {/* Logo image — left anchor */}
        <Link to="/" onClick={closeNav} style={{ textDecoration: 'none', flexShrink: 0, overflow: 'visible' }}>
          <img
            src={lpcLogo}
            alt="La Puerta Colombia"
            className="nav-logo"
            style={{ mixBlendMode: 'screen', display: 'block' }}
          />
        </Link>

        {/* Brand text — centred in the space between logo and nav links */}
        <Link to="/" onClick={closeNav} style={{
          textDecoration: 'none', flex: 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          marginLeft: 32,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: C.white, letterSpacing: -0.5 }}>LPC</span>
            <span style={{ fontSize: 24, fontWeight: 900, color: C.orange, letterSpacing: -0.5 }}>Talent</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.75)', letterSpacing: 1, textTransform: 'uppercase', marginTop: -2 }}>
            Global Care | Pasantías
          </div>
        </Link>

        <button className="nav-toggle" onClick={() => setNavOpen(!navOpen)} aria-label="Menu" aria-expanded={navOpen}>
          <span></span><span></span><span></span>
        </button>

        <div className={`nav-links${navOpen ? ' open' : ''}`} style={{ '--nav-bg': C.navy }}>

          {/* ── "Soy / I am / Ik ben" dropdown ── */}
          <div style={{ position: 'relative' }} onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
            <button
              onClick={() => setDropOpen(!dropOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontSize: 14, color: dropOpen ? C.orange : 'rgba(255,255,255,0.75)',
                display: 'flex', alignItems: 'center', gap: 5, transition: 'color 0.2s',
              }}
            >
              {soyLabel}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{
                transition: 'transform 0.2s',
                transform: dropOpen ? 'rotate(180deg)' : 'none',
              }}>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {dropOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 14px)', left: '50%',
                transform: 'translateX(-50%)',
                background: C.white, borderRadius: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                minWidth: 220, overflow: 'hidden', zIndex: 300,
              }}>
                {soyItems.map((item, i) => (
                  <Link key={i} to={item.href} onClick={closeNav} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '13px 18px', fontSize: 14, color: C.textDark,
                    textDecoration: 'none', borderBottom: i < soyItems.length - 1 ? `1px solid ${C.border}` : 'none',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `rgba(245,148,32,0.07)`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.orange, flexShrink: 0 }} />
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* ── Regular nav links ── */}
          {[
            ['/#intro',   t.nav_about],
            ['/#process', t.nav_how],
            ['/faq',      t.nav_faq],
            ['/#contact', t.nav_contact],
          ].map(([href, label]) => (
            <Link key={href} to={href} onClick={closeNav}
              style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', textDecoration: 'none', transition: 'color 0.2s' }}>
              {label}
            </Link>
          ))}
          <Link to="/#contact" onClick={closeNav} style={{
            background: C.orange, color: C.white, padding: '9px 22px',
            borderRadius: C.r, fontWeight: 700, fontSize: 14, textDecoration: 'none',
            transition: 'background 0.2s',
          }}>{t.nav_cta}</Link>
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<HomePage lang={lang} />} />
        <Route path="/faq" element={<FaqPage lang={lang} />} />
        <Route path="/globalcare" element={<GlobalCarePage lang={lang} />} />
        <Route path="/pasantias" element={<PasantiasPage lang={lang} />} />
        <Route path="/politica-de-privacidad" element={<PrivacyPage lang={lang} />} />
      </Routes>

      {/* ════════════════════════════════════════════════════ FOOTER */}
      <footer style={{ background: '#0a0d14', padding: '48px 48px 28px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="footer-grid" style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr',
            gap: 48, marginBottom: 32, paddingBottom: 32,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 20, fontWeight: 900, color: C.white }}>LPC </span>
                <span style={{ fontSize: 20, fontWeight: 900, color: C.orange }}>Talent</span>
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16 }}>
                Global Care | Pasantías
              </div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.8, maxWidth: 340 }}>
                {t.footer_about}
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: C.orange, marginBottom: 16 }}>
                {t.footer_programs}
              </h4>
              {[['/globalcare','LPC Global Care'],['/pasantias','LPC Pasantías']].map(([href, label]) => (
                <Link key={href} to={href} style={{ display:'block', fontSize:13, color:'rgba(255,255,255,0.4)', marginBottom: 8, textDecoration:'none' }}>{label}</Link>
              ))}
            </div>
            <div>
              <h4 style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: C.orange, marginBottom: 16 }}>
                {t.footer_contact}
              </h4>
              {[
                ['mailto:info@lapuerta.com.co','info@lapuerta.com.co'],
                ['https://wa.me/573059469665','WhatsApp: +57 305 946 9665'],
                ['https://www.lapuerta.com.co','lapuerta.com.co'],
              ].map(([href, label]) => (
                <a key={href} href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  style={{ display:'block', fontSize:13, color:'rgba(255,255,255,0.4)', marginBottom:8, textDecoration:'none' }}>
                  {label}
                </a>
              ))}
            </div>
          </div>
          <div className="footer-bottom" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.2)' }}>{t.footer_copy}</p>
            <div style={{ display:'flex', alignItems:'center', gap:24 }}>
              <Link to="/politica-de-privacidad" style={{ fontSize:12, color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>
                {t.footer_privacy}
              </Link>
              <p style={{ fontSize:12, color:'rgba(255,255,255,0.2)', fontStyle:'italic', margin:0 }}>{t.footer_tagline}</p>
            </div>
          </div>
        </div>
      </footer>

      {/* ════════════════════════════════════════════════════ WHATSAPP FLOATING BUTTON */}
      <a href="https://wa.me/573059469665" target="_blank" rel="noopener noreferrer"
        aria-label="Contact us on WhatsApp"
        style={{
          position: 'fixed', bottom: 28, left: 28,
          width: 56, height: 56, borderRadius: '50%',
          background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(37,211,102,0.45)', zIndex: 999, textDecoration: 'none',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.boxShadow='0 6px 24px rgba(37,211,102,0.6)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(37,211,102,0.45)'; }}
      >
        <IconWA size={26} />
      </a>

    </div>
    </BrowserRouter>
  );
}
